import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  ChartBar,
  CurrencyEur,
  Wrench,
  Warning,
  ArrowSquareOut,
  Bell,
  CheckCircle,
  FileXls,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fetchBudgetOverview, fetchBudgetHistory, updateTruck } from "@/lib/api";
import { useSites } from "@/lib/sites";
import { useAuth, filterSitesForUser, getUserAllowedSites } from "@/lib/auth";
import { useAutoRefresh } from "@/lib/useAutoRefresh";

function todayMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const fmtEur = (n) =>
  (Number(n) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";

const CAT_LABEL = {
  own: "NY TRANS",
  loue: "Loué",
};

function KpiCard({ label, value, sub, tone = "default", testId }) {
  const toneCls = {
    default: "border-zinc-900 bg-white text-zinc-900",
    good: "border-emerald-700 bg-emerald-50 text-emerald-900",
    bad: "border-red-700 bg-red-50 text-red-900",
    warn: "border-amber-500 bg-amber-50 text-amber-900",
    gold: "border-[var(--ny-gold)] bg-[var(--ny-gold)] text-zinc-900",
  }[tone];
  return (
    <div className={`border-2 ${toneCls} p-4`} data-testid={testId}>
      <div className="label-tag opacity-70">{label}</div>
      <div className="text-2xl md:text-3xl font-black mt-1 mono" style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
      {sub && <div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">{sub}</div>}
    </div>
  );
}

function NotifyAgencyDialog({ truck, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    method: "email",
    note: "",
    rental_frozen: false,
    freeze_days: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && truck) {
      setForm({
        method: truck.agency_notified_method || "email",
        note: truck.agency_notified_note || "",
        rental_frozen: !!truck.rental_frozen,
        freeze_days: truck.rental_freeze_days ? String(truck.rental_freeze_days) : "",
      });
    }
  }, [open, truck]);

  const submit = async () => {
    setSaving(true);
    try {
      await updateTruck(truck.truck_id, {
        agency_notified: true,
        agency_notified_at: new Date().toISOString(),
        agency_notified_method: form.method,
        agency_notified_note: form.note.trim(),
        rental_frozen: form.rental_frozen,
        rental_freeze_days: form.rental_frozen && form.freeze_days
          ? parseInt(form.freeze_days) || 0
          : 0,
      });
      toast.success("Agence notifiée");
      setOpen(false);
      onDone && onDone();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        onClick={() => setOpen(true)}
        className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-amber-500 text-amber-900 bg-amber-50 hover:bg-amber-100 inline-flex items-center gap-1"
        data-testid={`notify-agency-${truck.truck_id}`}
      >
        <Bell size={11} weight="bold" />
        {truck.agency_notified ? "Modifier notif." : "Agence prévenue ?"}
      </button>
      <DialogContent className="rounded-none border border-zinc-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-black uppercase tracking-tight" style={{ fontFamily: "Chivo" }}>
            Contacter l&apos;agence
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {truck.license_plate}{truck.rental_agency ? ` · ${truck.rental_agency}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tag">Moyen de contact</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="rounded-none border-zinc-900" data-testid="notify-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Téléphone</SelectItem>
                <SelectItem value="discord">Discord</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Note (n° dossier, personne contactée, etc.)</Label>
            <Textarea
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="rounded-none border-zinc-900"
              rows={3}
              data-testid="notify-note"
              placeholder="Ex : Mail envoyé à Sixt le 05/02, demande gel loc du 05/02 au retour du camion."
            />
          </div>
          <label className="flex items-center gap-2 border border-zinc-900 px-3 py-2 cursor-pointer bg-emerald-50">
            <input
              type="checkbox"
              checked={form.rental_frozen}
              onChange={(e) => setForm({ ...form, rental_frozen: e.target.checked })}
              data-testid="notify-freeze"
            />
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-900">
              Location confirmée gelée
            </span>
          </label>
          {form.rental_frozen && (
            <div>
              <Label className="label-tag">Durée du gel (jours)</Label>
              <Input
                type="number"
                value={form.freeze_days}
                onChange={(e) => setForm({ ...form, freeze_days: e.target.value })}
                className="rounded-none border-zinc-900 mono"
                min={1}
                placeholder="ex : 10"
                data-testid="notify-freeze-days"
              />
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                Combien de jours l&apos;agence gèle la location ? Au-delà, le loyer redevient perdu.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={() => setOpen(false)}
            className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40"
            data-testid="notify-submit"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BudgetPage() {
  const navigate = useNavigate();
  const { sites: allSites } = useSites();
  const { user } = useAuth();
  const sites = filterSitesForUser(allSites, user);
  const userScope = getUserAllowedSites(user); // null = admin/all
  // Default tab: if user is restricted to a single site, lock to it. Otherwise show "ALL".
  const defaultTab = userScope && userScope.length === 1 ? userScope[0] : "ALL";
  const [month, setMonth] = useState(todayMonthISO());
  const [siteTab, setSiteTab] = useState(defaultTab);
  const [data, setData] = useState({ totals: {}, trucks: [], alerts: [] });
  const [loading, setLoading] = useState(true);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const site = siteTab === "ALL" ? "" : siteTab;
      const d = await fetchBudgetOverview(month, site);
      setData(d);
    } catch {
      if (!silent) toast.error("Erreur chargement budget");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [month, siteTab]);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const totals = data.totals || {};
  const trucks = data.trucks || [];
  const alerts = data.alerts || [];

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    // Sheet 1 — Résumé
    const summaryRows = [
      ["Mois", month],
      ["Site", siteTab],
      ["Jours dans le mois", data.days_in_month || ""],
      [],
      ["Loyers payés (€)", totals.rent_paid || 0],
      ["Réparations (€)", totals.repairs || 0],
      ["Location gaspillée pannes (€)", totals.rent_lost || 0],
      ["Manque à gagner pannes (€)", totals.revenue_lost || 0],
      ["Perte totale liée aux pannes (€)", totals.total_loss || 0],
      ["Total dépensé + pertes (€)", totals.total_cost || 0],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
    summarySheet["!cols"] = [{ wch: 34 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, "Résumé");

    // Sheet 2 — Par camion
    const truckRows = [
      [
        "Immat.", "Marque", "Site", "Type", "Statut",
        "Loc. gelée", "Agence prévenue", "Agence",
        "Loyer mensuel", "CA/jour",
        "Jours panne (mois)", "Loyer payé (€)", "Loyer perdu (€)",
        "Manque à gagner (€)", "Réparations (€)", "Perte totale (€)",
      ],
      ...trucks.map((t) => [
        t.license_plate || "",
        t.brand || "",
        t.site || "",
        t.is_rented ? "Loué" : "NY TRANS",
        t.status || "",
        t.rental_frozen ? "Oui" : "Non",
        t.agency_notified ? "Oui" : "Non",
        t.rental_agency || "",
        t.monthly_rent || 0,
        t.daily_revenue || 0,
        t.days_broken_month || 0,
        t.rent_paid || 0,
        t.rent_lost || 0,
        t.revenue_lost || 0,
        t.repairs || 0,
        t.total_loss || 0,
      ]),
    ];
    const trucksSheet = XLSX.utils.aoa_to_sheet(truckRows);
    trucksSheet["!cols"] = [
      { wch: 12 }, { wch: 14 }, { wch: 8 }, { wch: 10 }, { wch: 10 },
      { wch: 12 }, { wch: 16 }, { wch: 22 },
      { wch: 16 }, { wch: 10 },
      { wch: 16 }, { wch: 14 }, { wch: 14 },
      { wch: 18 }, { wch: 14 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, trucksSheet, "Par camion");

    // Sheet 3 — Alertes
    if (alerts.length > 0) {
      const alertRows = [
        ["Immat.", "Agence", "Jours depuis panne", "Agence prévenue", "Perte estimée (€)"],
        ...alerts.map((a) => [
          a.license_plate || "",
          a.agency || "",
          a.days_since_panne || 0,
          a.agency_notified ? "Oui" : "Non",
          a.estimated_loss || 0,
        ]),
      ];
      const alertsSheet = XLSX.utils.aoa_to_sheet(alertRows);
      alertsSheet["!cols"] = [{ wch: 14 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, alertsSheet, "Alertes");
    }

    const fname = `budget-${siteTab}-${month}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success("Export Excel généré");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid="budget-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">NY Trans</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-1">
            <ChartBar size={28} weight="bold" className="inline mr-2 -mt-1 text-[var(--ny-gold)]" />
            Budget flotte
          </h1>
          <div className="ny-divider w-32 mt-2" />
          <p className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">
            Loyers · Réparations · Manque à gagner par panne · Suivi de gel des locations
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="label-tag">Mois</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="rounded-none border-zinc-900 w-44"
              data-testid="budget-month"
            />
          </div>
          <button
            onClick={exportExcel}
            disabled={loading || trucks.length === 0}
            className="inline-flex items-center gap-2 border border-emerald-700 bg-emerald-700 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-emerald-800 disabled:opacity-40 h-10"
            data-testid="budget-export-excel"
            title="Exporter le budget du mois en Excel"
          >
            <FileXls size={14} weight="bold" /> Excel
          </button>
        </div>
      </div>

      {/* Site tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-zinc-900 overflow-x-auto" data-testid="budget-site-tabs">
        {[
          ...sites.map((s) => ({ key: s.code, label: `${s.code}${s.name ? " · " + s.name : ""}` })),
          // Only show "Tous" tab if the user can actually see multiple sites
          ...(sites.length > 1 ? [{ key: "ALL", label: "Tous" }] : []),
        ].map((s) => {
          const active = siteTab === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSiteTab(s.key)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border border-b-0 whitespace-nowrap ${
                active
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
              }`}
              data-testid={`budget-tab-${s.key}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* 12-month sparkline chart */}
      <MonthlySparkline
        siteTab={siteTab}
        currentMonth={month}
        onSelectMonth={(m) => setMonth(m)}
      />

      {/* Alerts */}
      {alerts.length > 0 && (
        <div
          className="border-2 border-red-700 bg-red-50 mb-4"
          data-testid="budget-alerts"
        >
          <div className="bg-red-700 text-white px-3 py-1.5 flex items-center gap-2">
            <Warning size={14} weight="bold" />
            <span className="text-xs font-bold uppercase tracking-widest">
              {alerts.length} camion(s) loué(s) en panne · Location non gelée
            </span>
          </div>
          <div className="p-2 space-y-1">
            {alerts.map((a) => (
              <div
                key={a.truck_id}
                className="flex flex-wrap items-center justify-between gap-2 bg-white border border-red-300 px-3 py-2 text-sm"
                data-testid={`budget-alert-${a.truck_id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-bold mono">{a.license_plate}</span>
                  {a.agency && <span className="text-xs text-zinc-500">· {a.agency}</span>}
                  <span className="text-xs text-red-700 font-bold">
                    {a.days_since_panne} jours de panne
                  </span>
                  {a.agency_notified ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
                      <CheckCircle size={11} weight="bold" /> Agence prévenue
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-red-700">
                      Agence non prévenue
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs mono text-red-700 font-bold">
                    Perte estimée ce mois : {fmtEur(a.estimated_loss)}
                  </span>
                  <NotifyAgencyDialog truck={a} onDone={load} />
                  <button
                    onClick={() => navigate(`/trucks/${a.truck_id}`)}
                    className="p-1 hover:bg-zinc-100"
                    title="Voir le camion"
                    data-testid={`budget-alert-open-${a.truck_id}`}
                  >
                    <ArrowSquareOut size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Explanation banner */}
      <div className="border border-zinc-300 bg-zinc-50 px-4 py-3 mb-4 text-xs" data-testid="budget-explain">
        <div className="font-bold uppercase tracking-widest mb-1">Comment lire ces chiffres ?</div>
        <ul className="list-disc list-inside space-y-0.5 text-zinc-700">
          <li><b>Loyer réellement perdu</b> = loyer payé pour un camion loué qui n&apos;a pas roulé (panne non gelée ou gel dépassé).</li>
          <li><b>CA perdu</b> = tournées manquées à cause d&apos;une panne (à nous OU loué).</li>
          <li><b>Réparations</b> = factures des RDV du mois.</li>
          <li>Les pertes des pannes <b>déjà réparées ce mois</b> restent visibles (historique préservé).</li>
        </ul>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-6" data-testid="budget-kpis">
        <KpiCard label="Loyer réellement perdu" value={fmtEur(totals.rent_lost)} tone="warn" sub="loués non gelés" testId="kpi-rent-lost" />
        <KpiCard label="Chiffre d'affaires perdu" value={fmtEur(totals.revenue_lost)} tone="bad" sub="jours de panne" testId="kpi-revenue-lost" />
        <KpiCard label="Coût réparations" value={fmtEur(totals.repairs)} tone="default" sub={`${totals.breakdown_count || 0} panne(s)`} testId="kpi-repairs" />
        <KpiCard label="Loyers payés (info)" value={fmtEur(totals.rent_paid)} tone="default" sub="agences" testId="kpi-rent" />
        <KpiCard label="Coût total du mois" value={fmtEur(totals.total_cost)} tone="bad" sub="pertes + réparations + loyers" testId="kpi-total-cost" />
      </div>

      {/* Per-truck table */}
      <div className="border border-zinc-900 bg-white" data-testid="budget-trucks-table">
        <div className="grid grid-cols-12 bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-2">
          <div className="col-span-2">Camion</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-1 text-right">Loyer</div>
          <div className="col-span-1 text-right">J.panne</div>
          <div className="col-span-2 text-right">Réparations</div>
          <div className="col-span-2 text-right">Manque à gagner</div>
          <div className="col-span-2 text-right">Perte totale</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-xs uppercase tracking-widest text-zinc-500">
            Chargement…
          </div>
        ) : trucks.length === 0 ? (
          <div className="p-6 text-center text-xs uppercase tracking-widest text-zinc-500">
            Aucun camion trouvé pour ce filtre.
          </div>
        ) : (
          trucks.map((t) => {
            const isBad = (t.total_loss || 0) > 0;
            return (
              <div
                key={t.truck_id}
                onClick={() => navigate(`/trucks/${t.truck_id}`)}
                className={`grid grid-cols-12 border-t border-zinc-200 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer ${isBad ? "bg-red-50/40" : ""}`}
                data-testid={`budget-truck-row-${t.truck_id}`}
              >
                <div className="col-span-2 font-bold mono">
                  {t.license_plate}
                  <div className="text-[10px] text-zinc-500 font-normal uppercase tracking-widest">
                    {t.brand || "—"} · {t.site}
                  </div>
                </div>
                <div className="col-span-2 text-xs">
                  <div className="font-bold uppercase tracking-widest">
                    {t.is_rented ? (t.rental_agency || "Loué") : "NY TRANS"}
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                    {t.status}
                    {t.rental_frozen && <span className="ml-1 text-emerald-700">· gelé</span>}
                  </div>
                </div>
                <div className="col-span-1 text-right mono text-xs">
                  {t.is_rented ? fmtEur(t.rent_paid) : "—"}
                </div>
                <div className="col-span-1 text-right mono text-xs">
                  {t.days_broken_month > 0 ? (
                    <span className="text-red-700 font-bold">{t.days_broken_month}j</span>
                  ) : (
                    "—"
                  )}
                </div>
                <div className="col-span-2 text-right mono text-xs">
                  {t.repairs > 0 ? (
                    <span className="inline-flex items-center gap-1 justify-end">
                      <Wrench size={11} weight="bold" className="text-zinc-500" />
                      {fmtEur(t.repairs)}
                    </span>
                  ) : (
                    "—"
                  )}
                </div>
                <div className="col-span-2 text-right mono text-xs">
                  {t.revenue_lost > 0 ? (
                    <span className="text-red-700 font-bold">{fmtEur(t.revenue_lost)}</span>
                  ) : (
                    "—"
                  )}
                  {t.rent_lost > 0 && (
                    <div className="text-[10px] text-amber-700">
                      + {fmtEur(t.rent_lost)} loc
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-right mono text-xs">
                  {t.total_loss > 0 ? (
                    <span className="text-red-700 font-black">−{fmtEur(t.total_loss)}</span>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-3">
        Les pertes ne sont comptées que sur les jours de panne. Aucun CA n&apos;est simulé pour les camions qui roulent normalement.
      </div>

      {/* ---- Bilan annuel 12 mois ---- */}
      <YearlyBilan siteTab={siteTab} initialYear={parseInt(month.slice(0, 4))} />
    </div>
  );
}

function YearlyBilan({ siteTab, initialYear }) {
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState({ months: [], totals: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const site = siteTab === "ALL" ? "" : siteTab;
    fetchBudgetHistory(year, site)
      .then((d) => mounted && setData(d))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [year, siteTab]);

  const months = data.months || [];
  const totals = data.totals || {};
  const maxCost = Math.max(1, ...months.map((m) => m.total_cost || 0));
  const MONTH_NAMES = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

  return (
    <div className="mt-8" data-testid="yearly-bilan">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="label-tag">Bilan annuel</div>
          <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter" style={{ fontFamily: "Chivo" }}>
            {year} — 12 mois glissants
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setYear(year - 1)}
            className="px-2 py-1 text-xs font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
            data-testid="bilan-year-prev"
          >
            ←
          </button>
          <span className="mono font-bold px-3">{year}</span>
          <button
            onClick={() => setYear(year + 1)}
            className="px-2 py-1 text-xs font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
            data-testid="bilan-year-next"
          >
            →
          </button>
        </div>
      </div>
      {loading ? (
        <div className="border border-zinc-900 bg-white p-4 text-center text-xs uppercase tracking-widest text-zinc-500">
          Chargement…
        </div>
      ) : (
        <>
          <div className="border border-zinc-900 bg-white" data-testid="bilan-table">
            <div className="grid grid-cols-12 bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-2">
              <div className="col-span-2">Mois</div>
              <div className="col-span-1 text-right">Pannes</div>
              <div className="col-span-2 text-right">Loyer perdu</div>
              <div className="col-span-2 text-right">CA perdu</div>
              <div className="col-span-2 text-right">Réparations</div>
              <div className="col-span-3 text-right">Coût total</div>
            </div>
            {months.map((m) => {
              const pct = (m.total_cost / maxCost) * 100;
              const monthNum = parseInt(m.month.slice(-2));
              return (
                <div
                  key={m.month}
                  className={`grid grid-cols-12 border-t border-zinc-200 px-3 py-2 text-sm items-center ${m.total_cost > 0 ? "bg-red-50/20" : ""}`}
                  data-testid={`bilan-row-${m.month}`}
                >
                  <div className="col-span-2 font-bold mono">{MONTH_NAMES[monthNum - 1]} {year}</div>
                  <div className="col-span-1 text-right mono text-xs">
                    {m.breakdown_count > 0 ? <span className="text-red-700 font-bold">{m.breakdown_count}</span> : "—"}
                  </div>
                  <div className="col-span-2 text-right mono text-xs">
                    {m.rent_lost > 0 ? <span className="text-amber-700">{fmtEur(m.rent_lost)}</span> : "—"}
                  </div>
                  <div className="col-span-2 text-right mono text-xs">
                    {m.revenue_lost > 0 ? <span className="text-red-700 font-bold">{fmtEur(m.revenue_lost)}</span> : "—"}
                  </div>
                  <div className="col-span-2 text-right mono text-xs">
                    {m.repairs > 0 ? fmtEur(m.repairs) : "—"}
                  </div>
                  <div className="col-span-3 text-right mono text-xs relative">
                    {m.total_cost > 0 ? (
                      <>
                        <div
                          className="absolute inset-y-1 right-0 bg-red-100"
                          style={{ width: `${Math.max(6, pct)}%` }}
                        />
                        <span className="relative text-red-800 font-black">{fmtEur(m.total_cost)}</span>
                      </>
                    ) : "—"}
                  </div>
                </div>
              );
            })}
            <div className="grid grid-cols-12 bg-zinc-100 border-t-2 border-zinc-900 px-3 py-2 text-sm font-black">
              <div className="col-span-2 uppercase tracking-widest text-[11px]">Total {year}</div>
              <div className="col-span-1 text-right mono">{totals.breakdown_count || 0}</div>
              <div className="col-span-2 text-right mono">{fmtEur(totals.rent_lost)}</div>
              <div className="col-span-2 text-right mono">{fmtEur(totals.revenue_lost)}</div>
              <div className="col-span-2 text-right mono">{fmtEur(totals.repairs)}</div>
              <div className="col-span-3 text-right mono text-red-800">{fmtEur(totals.total_cost)}</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-2">
            Le bilan cumule les pannes déjà réparées + celles en cours + les factures RDV. Cliquer sur ← / → pour changer d&apos;année.
          </div>
        </>
      )}
    </div>
  );
}

const M_SHORT = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

function MonthlySparkline({ siteTab, currentMonth, onSelectMonth }) {
  const year = parseInt(currentMonth.slice(0, 4)) || new Date().getFullYear();
  const currentMonthIdx = parseInt(currentMonth.slice(5, 7)) - 1;
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const site = siteTab === "ALL" ? "" : siteTab;
    fetchBudgetHistory(year, site)
      .then((d) => mounted && setMonths(d.months || []))
      .catch(() => mounted && setMonths([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [year, siteTab]);

  const maxCost = Math.max(1, ...months.map((m) => m.total_cost || 0));
  const yearTotal = months.reduce((s, m) => s + (m.total_cost || 0), 0);
  const worst = months.reduce((acc, m) => (m.total_cost > (acc?.total_cost || 0) ? m : acc), null);

  return (
    <div
      className="border border-zinc-900 bg-white mb-4 relative"
      data-testid="budget-sparkline"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-zinc-50">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
            Coût total flotte · {year}
          </span>
          <span className="mono text-lg font-black text-red-800">{fmtEur(yearTotal)}</span>
          {worst && worst.total_cost > 0 && (
            <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
              Pire mois : <b className="text-zinc-800">{M_SHORT[parseInt(worst.month.slice(5, 7)) - 1]}. {year}</b> ({fmtEur(worst.total_cost)})
            </span>
          )}
        </div>
        <span className="text-[10px] text-zinc-400 uppercase tracking-widest hidden md:inline">
          Cliquer une barre pour zoomer sur ce mois
        </span>
      </div>

      {loading ? (
        <div className="px-3 py-6 text-center text-[10px] uppercase tracking-widest text-zinc-400">
          Chargement…
        </div>
      ) : (
        <div className="px-3 py-3">
          <div className="flex items-end gap-1.5 h-24" role="img" aria-label={`Coût mensuel ${year}`}>
            {months.map((m, i) => {
              const pct = maxCost > 0 ? (m.total_cost / maxCost) * 100 : 0;
              const height = m.total_cost > 0 ? Math.max(4, pct) : 2;
              const isCurrent = i === currentMonthIdx;
              const isEmpty = m.total_cost === 0;
              const barColor = isCurrent
                ? "bg-[var(--ny-gold)]"
                : isEmpty
                  ? "bg-zinc-200"
                  : "bg-red-700 hover:bg-red-800";
              return (
                <button
                  key={m.month}
                  type="button"
                  onClick={() => onSelectMonth && onSelectMonth(m.month)}
                  onMouseEnter={() => setHover({ m, i })}
                  onMouseLeave={() => setHover(null)}
                  className="flex-1 flex flex-col items-center gap-1 group"
                  data-testid={`sparkline-bar-${m.month}`}
                  aria-label={`${M_SHORT[i]} : ${fmtEur(m.total_cost)}`}
                >
                  <div className="w-full flex items-end h-20 relative">
                    <div
                      className={`w-full transition-all ${barColor} ${isCurrent ? "ring-2 ring-zinc-900" : ""}`}
                      style={{ height: `${height}%` }}
                    />
                  </div>
                  <div className={`text-[10px] mono ${isCurrent ? "font-black text-zinc-900" : "text-zinc-500"}`}>
                    {M_SHORT[i]}
                  </div>
                </button>
              );
            })}
          </div>
          {hover && (
            <div className="mt-2 flex items-center gap-3 text-[11px] mono border-t border-zinc-100 pt-2" data-testid="sparkline-tooltip">
              <span className="font-black uppercase tracking-widest text-zinc-800">
                {M_SHORT[hover.i]}. {year}
              </span>
              <span className="text-red-700 font-bold">Coût : {fmtEur(hover.m.total_cost)}</span>
              {hover.m.breakdown_count > 0 && (
                <span className="text-zinc-500">{hover.m.breakdown_count} panne(s)</span>
              )}
              {hover.m.repairs > 0 && (
                <span className="text-zinc-500">Rép. {fmtEur(hover.m.repairs)}</span>
              )}
              {hover.m.rent_lost + hover.m.revenue_lost > 0 && (
                <span className="text-amber-700">Perte {fmtEur(hover.m.rent_lost + hover.m.revenue_lost)}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

